/* eslint-disable camelcase */
import { useCallback } from 'react';
import { BrazeEvents, trackBrazeEvent } from '@blocs.braze';
import { Profile } from '@minecraft.graphql-operations';
import { gqlSystemRoleEnum } from '@minecraft.graphql-types';
import { publishEvent } from './publish-event';
import { PlanType } from '../Paywall/usePlans';
import { OPEN_CALL_QUERY_PARAMS } from '../Auth';
import { addUserProperties } from '../Heap';

const CARDLESS_CAMPAIGN_UTM_SUFFIX = 'CFT';
const CARDLESS_SIGNUP_GTM_EVENT = 'cardless_signup';
const CARDLESS_TRIAL_START_GTM_EVENT = 'trial_start';

export const useTrackRegistration = () => {
  const trackRegistrationCompleteEvent = useCallback(
    ({
      selectedPlan,
      profileDetails,
      redirectToOpenCall,
    }: {
      selectedPlan?: PlanType;
      profileDetails?: Profile;
      redirectToOpenCall?: boolean;
    }) => {
      try {
        if (redirectToOpenCall) {
          trackBrazeEvent(BrazeEvents.OpenCallRegistrationComplete, {
            date_created: profileDetails?.created,
            plan_code: selectedPlan?.billingCode,
          });
          return;
        }

        if (selectedPlan?.isCardlessTrial) {
          trackBrazeEvent(BrazeEvents.CardlessSignupCompleted);
          publishEvent({ event: CARDLESS_TRIAL_START_GTM_EVENT });
          return;
        }
      } catch (error) {
        console.error('Error tracking open call registration braze event', error);
      }
    },
    []
  );

  const trackRegistrationInitiatedEvent = useCallback(
    ({
      utmSource,
      utmCampaign,
      utmMedium,
      systemRoleCode,
    }: {
      utmSource?: string;
      utmCampaign?: string;
      utmMedium?: string;
      systemRoleCode?: gqlSystemRoleEnum;
    }) => {
      const isCardlessTrialSignup = utmCampaign?.endsWith(CARDLESS_CAMPAIGN_UTM_SUFFIX);

      if (String(utmSource) === OPEN_CALL_QUERY_PARAMS.utm_source) {
        trackBrazeEvent(BrazeEvents.OpenCallInitialRegistrationComplete, {
          date_created: new Date().toISOString(),
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_medium: utmMedium,
        });
      }

      if (isCardlessTrialSignup) {
        trackBrazeEvent(BrazeEvents.CardlessSignupStarted);
        publishEvent({ event: CARDLESS_SIGNUP_GTM_EVENT });
        addUserProperties({ isCardlessTrialSignup: true });
      }

      if (systemRoleCode && [gqlSystemRoleEnum.ProjectCreator].includes(systemRoleCode)) {
        trackBrazeEvent(BrazeEvents.PCRegistrationPageComplete);
      }
    },
    []
  );

  return {
    trackRegistrationCompleteEvent,
    trackRegistrationInitiatedEvent,
  };
};
